import React, { useContext, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import {
  ApiFilterOperation,
  Autocomplete,
  TableContext,
  TableFilter,
  TableFilterState,
  TextField,
} from '@eas/common-web';
import { useDepartmentAutocomplete } from '@modules/department/department-api';
import { ParsedTableNamedSettingsContext } from '@composite/custom-named-settings/table-named-settings/table-named-settings-context';
import { DepartmentAutocomplete, DictionaryAutocomplete } from '@models';
import { labelMapper } from '@utils';
import { PlannerContext } from '../planner-context';

export function WorkOrderTableCustomSearchBar() {
  return (
    <Box
      display="flex"
      alignItems="center"
      width="100%"
      justifyContent="spave-between"
      style={{ marginBottom: 8 }}
    >
      <WorkOrderIdField />
      <DepartmentFilterField />
    </Box>
  );
}

const getFilterIndex = (filterKey: string, filters: TableFilter[]) => {
  return filters?.findIndex((f) => f.filterkey === filterKey);
};

const useStyles = makeStyles(() => ({
  departmentAutocomplete: {
    '& [data-cy="EAS__AUTOCOMPLETE"] > div > div:nth-child(2)': {
      maxHeight: 72,
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingRight: 4,
    },
  },
}));

const WorkOrderIdField = () => {
  const { filters, setFiltersState, filtersState } = useContext(TableContext);
  const { selectSaved } = useContext(ParsedTableNamedSettingsContext);
  const { calendarWorkOrderTableFilterIds, clearCalendarWorkOrderTableFilter } =
    useContext(PlannerContext);

  const filterIndex = getFilterIndex('workOrderId', filters);

  const state = filtersState[filterIndex];

  const initialValue = state.value
    ? (state.value as string)
    : state?.filters?.[0]?.values?.join(' ') ?? '';

  const [stringValue, setStringValue] = useState(
    state.enabled ? initialValue : ''
  );

  useEffect(() => {
    if (state.enabled) {
      const initialValue = state.value
        ? (state.value as string)
        : state?.filters?.[0]?.values?.join(' ') ?? '';
      setStringValue(initialValue);
    } else {
      setStringValue('');
    }
  }, [state]);

  const handleClear = () => {
    clearCalendarWorkOrderTableFilter();
    setFiltersState?.((p) => {
      const newState = [...p];
      newState[filterIndex] = {
        ...newState[filterIndex],
        operation: ApiFilterOperation.AND,
        enabled: false,
        value: null,
        filters: [],
      };
      return newState;
    });
    selectSaved(undefined);
  };

  const handleClearCalendarFilter = () => {
    clearCalendarWorkOrderTableFilter();
    selectSaved(undefined);
  };

  const handleSubmit = () => {
    if (filterIndex === -1) {
      return;
    }

    clearCalendarWorkOrderTableFilter();

    const woIds = stringValue
      .trim()
      .split(' ')
      .map((w) => w.trim())
      .filter((s) => s);

    if (woIds?.length === 0) {
      setFiltersState?.((p) => {
        const newState = [...p];
        newState[filterIndex] = {
          ...newState[filterIndex],
          operation: ApiFilterOperation.AND,
          enabled: false,
          value: null,
          filters: [],
        };
        return newState;
      });
      selectSaved(undefined);
      return;
    }

    setFiltersState?.((p) => {
      // https://jira-sm.zsdis.sk/browse/FSMS-920
      // chceli aby zadanie do searchu zresetovalo vsetky filtre
      // ak budu chciet vratit toto rozhodnutie - // const newState = [...p];
      const newState: TableFilterState[] = p.map((state) => ({
        ...state,
        value: null,
        enabled: false,
        object: null,
        filters: [],
        selectFilter: [],
        numberFilter: null,
      }));
      /////////////////////////////////////

      newState[filterIndex] = {
        ...newState[filterIndex],
        enabled: true,
        operation: ApiFilterOperation.AND,
        value: stringValue.trim(),
        filters: [
          {
            operation: ApiFilterOperation.IN,
            field: 'workOrderId',
            values: woIds,
          },
        ],
      };
      return newState;
    });

    selectSaved(undefined);
  };

  return (
    <Box
      style={{
        width: '50%',
        paddingRight: 16,
        minWidth: 400,
      }}
    >
      <Box display="flex" alignItems="center" style={{ minHeight: 18 }}>
        <Typography style={{ lineHeight: 0.8, fontSize: 'small' }}>
          Čísla zákaziek:
        </Typography>
        {calendarWorkOrderTableFilterIds?.length ? (
          <Box
            display="flex"
            alignItems="center"
            ml={1}
            bgcolor="rgb(228, 98, 108)"
            px={1}
          >
            <Typography style={{ fontSize: 'small', color: 'white' }}>
              Aktívne striktné filtrovanie z kalendára:{' '}
              {calendarWorkOrderTableFilterIds.length} PP
            </Typography>
            <IconButton
              onClick={handleClearCalendarFilter}
              size="small"
              title="Zrušiť filtrovanie z kalendára"
              color="inherit"
            >
              <ClearIcon fontSize="small" style={{ color: 'white' }} />
            </IconButton>
          </Box>
        ) : null}
      </Box>
      <Box display="flex" width="90%">
        <Box display="block" alignItems="center" width="100%">
          <TextField
            style={{ width: '100%', maxHeight: 200 }}
            multiline
            inputProps={{ rowsMax: 3, maxLength: 10000 }}
            endAdornment={
              <IconButton onClick={handleSubmit} size="small">
                <SearchIcon />
              </IconButton>
            }
            value={stringValue}
            onChange={(value) => setStringValue(value ?? '')}
            onClear={handleClear}
            showClearButton
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
          {/* {stringValue
            .split(' ')
            .filter((id) => id.trim() !== '')
            .map((id) => (
              <Chip
                key={id}
                label={id}
                clickable
                deleteIcon={<ClearIcon fontSize="small" />}
                onDelete={() => {
                  setStringValue(
                    stringValue
                      .split(' ')
                      .filter((i) => i.trim() !== id)
                      .join(' ')
                  );
                }}
                style={{
                  padding: 0,
                  fontSize: 11,
                  height: 20,
                  marginRight: 2,
                  background: 'transparent',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                }}
              />
            ))} */}
        </Box>
      </Box>
    </Box>
  );
};

function DepartmentFilterField() {
  const classes = useStyles();
  const { filters, setFiltersState, filtersState } = useContext(TableContext);
  const depFilterState =
    filtersState[getFilterIndex('department.id', filters)]?.object ?? null;
  const [values, setValues] = useState<DictionaryAutocomplete[] | null>(
    depFilterState
  );

  const { selectSaved } = useContext(ParsedTableNamedSettingsContext);

  const source = useDepartmentAutocomplete();

  useEffect(() => {
    setValues(depFilterState);
  }, [depFilterState]);

  return (
    <Box
      style={{
        width: '50%',
        paddingRight: 8,
        minWidth: 500,
      }}
    >
      <Typography style={{ lineHeight: 0.8, fontSize: 'small' }}>
        Pracovisko:
      </Typography>
      <Box display="flex" alignItems="center">
        <Box className={classes.departmentAutocomplete} width="100%">
          <Autocomplete<DepartmentAutocomplete>
            source={source}
            endAdornment={<></>}
            labelMapper={labelMapper}
            onChange={async (values) => {
              setValues(values as DictionaryAutocomplete[]);
            }}
            value={values}
            multiple={true}
            clearable={false}
          />
        </Box>
        <Box display="flex" alignItems="center" ml={3}>
          <Button
            variant="contained"
            disabled={!values || values.length === 0}
            color="primary"
            onClick={() => {
              const index = getFilterIndex('department.id', filters);

              if (index === -1) {
                return;
              }
              if (values?.length === 0) {
                setFiltersState?.((p) => {
                  const newState = [...p];
                  newState[index] = {
                    ...newState[index],
                    operation: ApiFilterOperation.OR,
                    enabled: false,
                    object: null,
                    filters: [],
                  };
                  return newState;
                });
                selectSaved(undefined);
                return;
              }

              setFiltersState?.((p) => {
                const newState = [...p];
                newState[index] = {
                  ...newState[index],
                  enabled: true,
                  object: values,
                  operation: ApiFilterOperation.OR,
                  filters: [
                    {
                      operation: ApiFilterOperation.IN,
                      field: 'department.id',
                      values: values?.map((v) => v.id) ?? [],
                    },
                  ],
                };
                return newState;
              });
              selectSaved(undefined);
            }}
            size="small"
          >
            <SearchIcon fontSize="small" />
          </Button>
          <Button
            disabled={!values || values.length === 0}
            size="small"
            onClick={() => {
              setValues([]);
              const index = getFilterIndex('department.id', filters);
              if (index === -1) {
                return;
              }
              setFiltersState?.((p) => {
                const newState = [...p];
                newState[index] = {
                  ...newState[index],
                  enabled: false,
                  object: null,
                  filters: [],
                };
                return newState;
              });
              selectSaved(undefined);
            }}
          >
            <ClearIcon fontSize="small" />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
